import React, { type ReactNode } from 'react';

import Box from '@amzn/meridian/box';
import Divider from '@amzn/meridian/divider';
import RadioButton from '@amzn/meridian/radio-button';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { type ErrorRecord, type HandleChangeDynamicKey } from 'src/hooks/useForm';
import { type DownloadTWPAForm } from 'src/store/placementDetailsPageSlice/types';

// ================================= Types ================================= //

interface RadioOption {
  value: any;
  ariaLabel: string;
  label: {
    header: string;
    body: string;
  };
}

interface TWPSelectorProps {
  textInfo: ReactNode;
  formDataKey: keyof DownloadTWPAForm;
  radioOptions: RadioOption[];
  twpaFormData: Partial<DownloadTWPAForm>;
  twpaFormErrors: ErrorRecord<DownloadTWPAForm>;
  handleChange: HandleChangeDynamicKey<DownloadTWPAForm>;
}

// =============================== Components ============================== //

/**
 * Default Component
 */
const TWPSelector = ({
  textInfo,
  radioOptions,
  formDataKey,
  twpaFormData,
  twpaFormErrors,
  handleChange,
}: TWPSelectorProps) => {
  const errorObject = twpaFormErrors[formDataKey];

  return (
    <div>
      <Box spacingInset="none none 200 none">
        <Text>{textInfo ?? ''}</Text>
      </Box>

      {/* Error Text */}
      <Text color="error">{errorObject?.error ? errorObject?.errorMessage : ''}</Text>

      {/* Radio Buttons */}
      {radioOptions.map((option, index) => (
        <Row key={`${index}-${option.value}`} widths={['fill']} spacingInset="200" alignmentVertical="top">
          <RadioButton
            checked={twpaFormData[formDataKey] === option.value}
            onChange={(value) => handleChange(formDataKey, value)}
            value={option.value}
            aria-label={option.ariaLabel}
          >
            <Box>
              <Text>
                <strong>{option.label.header}</strong>
              </Text>
              <Text>{option.label.body}</Text>
            </Box>
          </RadioButton>
        </Row>
      ))}

      <Divider spacingBefore="400" spacingAfter="400" />
    </div>
  );
};

// ================================= Export ================================ //

export default TWPSelector;
